<template>
  <transition name="down">
    <div
      v-if="menu"
      class="mega-nav container"
    >
      <div
        class="
          mega-nav__row-block
          container
          container--full
        "
      >
        <ul class="mega-nav__row-list">
          <li
            v-for="(link, index) of menu"
            :key="link.handle"
            class="mega-nav__row-item"
            :class="{
              [$class('active')]: activeIndex === index,
            }"
          >
            <a
              :href="link.url"
              class="mega-nav__row-link text-body-s-regular"
              :style="headerLinkStyle(linkStyles, link.title)"
              @click.prevent="setActiveClass(index)"
              v-text="link.title"
            />
          </li>
        </ul>
      </div>

      <div class="mega-nav__col-block container container--full grid">
        <div
          class="col"
          :class="menuClass"
        >
          <ul class="mega-nav__child-list">
            <li
              v-if="showViewAllLink"
              class="mega-nav__child-item"
            >
              <a
                :href="activeViewAllUrl"
                class="text-body-s-bold"
                v-text="$string('general.mega_nav.view_all')"
              />
            </li>

            <li
              v-for="link of linksTakenFromActiveIndex"
              :key="link.handle"
              class="mega-nav__child-item"
            >
              <a
                :href="link.url"
                :style="headerLinkStyle(linkStyles, link.title)"
                class="text-body-s-regular"
                v-text="link.title"
              />
            </li>
          </ul>
        </div>

        <!-- Additional content -->
        <template
          v-for="(content, index) of filteredMegaNavContents"
          :key="index"
        >
          <div
            v-for="(item, titleIndex) of content.titles"
            :key="`title-${titleIndex}`"
            class="col xs-span mega-nav__content-col"
            :class="{
              'l9-12': content.titles.length === 2 && titleIndex === 0,
              'l4': content.titles.length === 2 && titleIndex === 1,
              'l13-16': content.titles.length === 1 && titleIndex === 0,
            }"
          >
            <template
              v-if="
                (
                  content.urls[titleIndex]?.url &&
                  content.images[titleIndex]?.image
                )
              "
            >
              <a
                class="mega-nav__content-block"
                :href="content.urls[titleIndex].url"
              >
                <responsive-image
                  class="mega-nav__content-image"
                  :image="content.images[titleIndex].image"
                  ratio="1:1"
                  sizes="(min-width: 1024px) 25vw, 50vw"
                />

                <div class="mega-nav__content-text-block">
                  <p class="mega-nav__content-text text-subtitle-s">
                    {{ content.titles[titleIndex].title }}
                  </p>
                </div>
              </a>
            </template>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>

/**
 * Vue: Mega-Nav (mega-nav)
 * -----------------------------------------------------------------------------
 * Mega Nav.
 *
 * @param {Object} menu - Current mega-nav menu.
 * @param {Object} meganavContent - Mega-nav content.
 * @param {String} menuHandle - Tier1 menu handle.
 * @param {Object} linkStyles - Link styles.
 *
 */
import { headerLinkStyle } from '~/helpers/general'
import ResponsiveImage from '~global/images/responsive-image.vue'

export default {
  name: 'MegaNav',

  components: {
    ResponsiveImage,
  },

  props: {
    menu: {
      type: Array,
      default: () => [],
    },
    megaNavContent: {
      type: Object,
      default: () => ({}),
    },
    menuHandle: {
      type: String,
      default: '',
    },
    linkStyles: {
      type: Object,
      default: () => ({}),
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      navTimeout: null,
      megaNav: {
        currentChildMenu: null,
      },
      activeIndex: 0,
      currentMenuUrl: '',
    }
  },

  computed: {

    /**
     * Compute links for menu depends on active index value.
     * @return {Array}
     */
    linksTakenFromActiveIndex() {
      if (!this.menu || this.menu.length === 0) {
        return []
      }

      const activeMenu = this.menu[this.activeIndex]

      if (activeMenu && activeMenu.links && Array.isArray(activeMenu.links)) {
        return activeMenu.links
      }

      return []
    },

    /**
     * Compute mega-nav additional content.
     * @return {Array}
     */
    filteredMegaNavContents() {
      if (!Array.isArray(this.megaNavContent)) {
        return []
      }

      return this.megaNavContent.filter((content) => {
        return content.menu_handle === this.currentMenuHandle
      })
    },

    /**
     * Set url to the view-all link.
     * @return {String}
     */
    activeViewAllUrl() {
      if (!this.menu || this.menu.length === 0) {
        return '#'
      }

      const activeMenu = this.menu[this.activeIndex]
      return activeMenu ? activeMenu.url : '#'
    },

    /**
     * Change menu class name depends on additional content length.
     * @return {String}
     */
    menuClass() {
      if (!this.filteredMegaNavContents || this.filteredMegaNavContents.length === 0) {
        return 'xs-span'
      }

      switch (this.filteredMegaNavContents[0].images.length) {
        case 0:
          return 'xs-span'
        case 1:
          return 'l12'
        case 2:
        default:
          return 'l8'
      }
    },

    /**
     * Compute the current menu handle based on the active index.
     * @return {String} - The handle of the current active menu item.
     */
    currentMenuHandle() {
      if (this.menu && this.menu.length > this.activeIndex) {
        return this.menu[this.activeIndex]?.handle || ''
      }

      return ''
    },

    /**
     * Compute if view all link should be shown in current tier.
     * @returns {Boolean}
     */
    showViewAllLink() {
      return this.$variable('settings.viewAllHandles').includes(this.currentMenuHandle)
    },
  },

  watch: {

    /**
     * Watches if mega-nav overlay is closed to reset mega-nav child tier to
     * the initial tier position.
     * @param {Number} newValue - Index of current active menu.
     */
    'overlay.isActive'(newValue) {
      if (!newValue) {
        this.activeIndex = 0
      }
    },

    /**
     * Watches for tier1 menu handle to reset activeIndex.
     * @param {String} newValue - New menu handle.
     * @param {String} oldValue - Old menu handle.
     */
    menuHandle(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.activeIndex = 0
      }
    },
  },

  mounted() {

    /**
     * Watch menu to get current menu handle.
     */
    if (!this.menu || this.menu.length === 0) {
      return
    }

    this.currentMenuHandle = this.menu[this.activeIndex]?.handle || ''
  },

  methods: {

    /**
     * Link style.
     */
    headerLinkStyle,

    /**
     * Set active class.
     * @param {Number} index - Index of current active menu item.
     */
    setActiveClass(index) {
      this.activeIndex = index
    },
  },
}
</script>
